import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Stethoscope, Building2, ClipboardList, Plane, Home, HeartPulse,
  ArrowRight, CheckCircle
} from "lucide-react";

const services = [
  {
    icon: Stethoscope,
    title: "Medical Advisory",
    desc: "Expert medical consultation and second opinions from top specialists",
    details: [
      "Initial medical assessment and case review",
      "Second opinion from leading specialists",
      "Treatment option analysis and recommendations",
      "Video consultations with doctors before travel",
      "Detailed cost estimates and treatment timelines",
    ],
  },
  {
    icon: Building2,
    title: "Hospital Coordination",
    desc: "Seamless coordination with top accredited hospitals across Kerala, India",
    details: [
      "Hospital selection based on your medical needs",
      "Appointment scheduling and confirmation",
      "Medical records transfer and management",
      "Pre-admission procedures and documentation",
      "Dedicated hospital liaison for your stay",
    ],
  },
  {
    icon: ClipboardList,
    title: "Treatment Planning",
    desc: "Comprehensive treatment plans with transparent pricing",
    details: [
      "Personalized treatment roadmap",
      "Transparent cost breakdown with no hidden fees",
      "Timeline and milestone planning",
      "Alternative treatment options comparison",
      "Insurance coordination and documentation",
    ],
  },
  {
    icon: Plane,
    title: "Travel & Logistics",
    desc: "Complete travel support including visa, flights, and transfers",
    details: [
      "Medical visa application assistance",
      "Flight booking and itinerary planning",
      "Airport pickup and drop-off services",
      "Local transportation arrangements",
      "SIM card and communication setup",
    ],
  },
  {
    icon: Home,
    title: "Accommodation Support",
    desc: "Comfortable stays near your treatment center",
    details: [
      "Hospital-adjacent accommodation options",
      "Budget to luxury stay arrangements",
      "Dietary and cultural preference accommodations",
      "Extended stay options for recovery period",
      "Family accommodation coordination",
    ],
  },
  {
    icon: HeartPulse,
    title: "Post-Treatment Care",
    desc: "Ongoing support and follow-up after your treatment",
    details: [
      "Comprehensive discharge and care plan",
      "Virtual follow-up consultations",
      "Medication management and prescriptions",
      "Local doctor coordination in your country",
      "24/7 emergency support hotline",
    ],
  },
];

export default function ServicesPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="services-page">
      <SEO title="Our Services" description="Complete medical tourism services - medical consultation, hospital coordination, treatment planning, travel assistance, accommodation, and post-treatment care." path="/services" keywords="medical tourism services, hospital coordination India, medical travel assistance" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <span className="overline">{t("servicesPage.overline")}</span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
              {t("servicesPage.title")} <span className="font-medium text-[#00A4A9]">{t("servicesPage.titleHighlight")}</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl mb-16">
              {t("servicesPage.desc")}
            </p>
          </FadeIn>
          <div className="space-y-8">
            {services.map((svc, i) => (
              <FadeIn key={svc.title} delay={i * 60}>
                <div
                  className="bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] card-hover"
                  data-testid={`service-detail-${i}`}
                >
                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 rounded-2xl bg-[#e0f2fe] flex items-center justify-center">
                        <svc.icon className="w-7 h-7 text-[#00A4A9]" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h2 className="font-outfit text-2xl font-medium text-[#03045e] mb-3">{svc.title}</h2>
                      <p className="text-slate-600 mb-5">{svc.desc}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {svc.details.map((d) => (
                          <div key={d} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-[#00A4A9] mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-slate-600">{d}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="hidden">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-white mb-5 tracking-tight">
              {t("servicesPage.ctaTitle")}
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              {t("servicesPage.ctaDesc")}
            </p>
            <Link to="/contact" data-testid="services-cta-btn">
              <Button
                size="lg"
                className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white px-8 h-12 btn-hover"
              >
                {t("hero.cta1")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
